import Dexie from 'dexie';

/**
 * IndexedDB schema (Dexie.js) — store local pour le mode hors-ligne.
 *
 * Tables :
 *  - meta              : clé/valeur (token, user, lastSync, etc.)
 *  - visits            : visites téléchargées + brouillons hors-ligne
 *  - bordereau         : items BPU (un seul user)
 *  - studies           : études (lecture cache + édition locale)
 *  - photos            : blobs photos en attente d'upload
 *  - sync_queue        : actions hors-ligne à rejouer (CREATE / UPDATE / DELETE)
 *  - conflict_log      : conflits détectés au moment du push (audit)
 *
 * Convention : chaque entité possède un champ `_local_status` :
 *   - "synced"   = à jour avec serveur
 *   - "pending"  = en attente d'upload
 *   - "conflict" = upload refusé (à inspecter)
 * et un champ `_updated_at_local` (ISO) pour le LWW.
 */

class MdbOfflineDB extends Dexie {
  constructor() {
    super('mdb_offline_v1');
    this.version(1).stores({
      meta: 'key',
      visits: 'id, property_id, _local_status, _updated_at_local',
      bordereau: 'id',
      studies: 'id, property_id, _local_status, _updated_at_local',
      photos: '++id, visit_id, project_id, _local_status, created_at',
      sync_queue: '++queue_id, entity, action, entity_id, created_at',
      conflict_log: '++id, entity, entity_id, created_at',
    });
  }
}

export const offlineDB = new MdbOfflineDB();

// === META helpers ===
export async function getMeta(key) {
  const row = await offlineDB.meta.get(key);
  return row?.value;
}

export async function setMeta(key, value) {
  await offlineDB.meta.put({ key, value });
}

// === Sync queue helpers ===
export async function enqueueAction({ entity, action, entity_id, payload, optimistic = true }) {
  return offlineDB.sync_queue.add({
    entity,
    action, // 'create' | 'update' | 'delete' | 'upload_photo'
    entity_id,
    payload,
    optimistic,
    created_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
  });
}

export async function pendingCount() {
  return offlineDB.sync_queue.count();
}

export async function clearAll() {
  await Promise.all([
    offlineDB.meta.clear(),
    offlineDB.visits.clear(),
    offlineDB.bordereau.clear(),
    offlineDB.studies.clear(),
    offlineDB.photos.clear(),
    offlineDB.sync_queue.clear(),
    offlineDB.conflict_log.clear(),
  ]);
}

export default offlineDB;
